import * as React from 'react';

import { connect } from 'react-redux';

import styled from 'styled-components';

import AppState from '@webapp/store/state';
import { ImageWithPositionPlaceHolder } from '@webapp/store/merch/state';

import { AssetFile } from '@common/types/data/asset-file';

import { MerchProductPositionWithHeight } from '@services/merch/types/api/create-product';
import {
  UploadImageRequest,
  UploadImageResponse,
} from '@services/merch/types/api/upload-image';

import { DropdownCtrl } from '@webapp/controllers/form/dropdown';

import Grid from '@components/basics/grid';
import ErrorMessage from '@components/basics/error-message';

interface MangeArtProps {
  images: Partial<
    UploadImageRequest &
      UploadImageResponse &
      ImageWithPositionPlaceHolder &
      Partial<AssetFile>
  >[];
  updateMerchImages: Function;
  availablePositions: MerchProductPositionWithHeight[];
  className?: string;
}

interface PositionSelectDropDownProps {
  value: string;
  onChangePosition: (position: string) => void;
  availablePositions: MerchProductPositionWithHeight[];
  className?: string;
}
const PositionSelectDropDown = ({
  value,
  onChangePosition,
  availablePositions,
}: PositionSelectDropDownProps) => {
  const positionOptions = availablePositions?.map((position) => {
    const positionString = position.position;
    return {
      value: positionString,
      label: positionString,
    };
  });
  return (
    <DropdownCtrl
      className="position-dropdown"
      headerClassName="position-dropdown-header"
      isCapitaliseOptions
      value={value}
      options={positionOptions}
      onChange={(value) => onChangePosition(value)}
    />
  );
};

interface ImagePositionProps {
  image?: Partial<
    UploadImageRequest &
      UploadImageResponse &
      ImageWithPositionPlaceHolder &
      Partial<AssetFile>
  >;
  onChangePosition: (id: string, position: string) => void;
  availablePositions: MerchProductPositionWithHeight[];
  className?: string;
}

const ImageAndPosition = (props: ImagePositionProps) => {
  const { image, onChangePosition, availablePositions } = props;
  const onChangePositionImage = (position: string) => {
    if (image?.id) {
      onChangePosition(image.id, position);
    }
  };

  return (
    <Wrapper>
      <Image src={image?.url} />
      <PositionSelectDropDown
        value={image?.position || 'front'}
        availablePositions={availablePositions}
        onChangePosition={onChangePositionImage}
        className={props.className}
      />
    </Wrapper>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    availablePositions: state.merch.active?.availablePositions,
  };
};

const ManageArtMerch = (props: MangeArtProps) => {
  const { images, updateMerchImages, availablePositions } = props;

  const [merchImages, setMerchImages] = React.useState([...images]);
  const [isDuplicatedPosition, setIsDuplicatedPosition] = React.useState(false);

  React.useEffect(() => {
    setMerchImages([...images]);
  }, [images]);

  const onChangeImagePosition = (imageId: string, position: string) => {
    const newImages = [...merchImages];
    const imageChangedIndex = newImages?.findIndex(
      (image) => image.id === imageId
    );
    const imageTobeChanged = newImages[imageChangedIndex];

    newImages[imageChangedIndex] = { ...imageTobeChanged, position };

    const isPositionChanged = newImages.find((newImage) => {
      const oldImage = images.find((image) => image.id === newImage.id);
      return oldImage?.position !== newImage.position;
    });

    setMerchImages(newImages);
    if (isPositionChanged) {
      const isDuplicatedImagePosition = merchImages.some(
        (image) => image.position === position
      );

      setIsDuplicatedPosition(isDuplicatedImagePosition);

      if (!isDuplicatedImagePosition) {
        updateMerchImages(newImages);
      }
    } else {
      setIsDuplicatedPosition(false);
    }
  };

  //here on change of drop down of position we should change dispatch an action to update the store
  // start from here
  return (
    <>
      <ArtWrapper className={props.className} gap={3}>
        {merchImages?.length &&
          merchImages.map((img) => {
            return (
              <ImageAndPosition
                key={img.id}
                image={img}
                onChangePosition={onChangeImagePosition}
                className={props.className}
                availablePositions={availablePositions}
              />
            );
          })}
      </ArtWrapper>
      {isDuplicatedPosition && (
        <ErrorMessage
          className="mt-10"
          hasBackground
          errorId="MERCH.SECTION.DESIGN.MANAGE.ART.ERROR"
        />
      )}
    </>
  );
};

export default connect(mapStateToProps)(styled(ManageArtMerch)`
  .position-dropdown {
    position: relative;
    .position-dropdown-header {
      border-radius: 0 0 4px 4px;
      height: 44px;
      padding-top: 0;
      padding-bottom: 0;
      min-height: auto;
    }
  }
`);

const Image = styled.img`
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: 4px 4px 0 0;
`;

const ArtWrapper = styled(Grid)`
  grid-template-columns: repeat(3, minmax(0, 1fr));
`;

const Wrapper = styled(Grid)`
  grid-gap: 0;
`;
